// src/pages/CreateImage.js
// Image studio with three modes:
//   Text   → POST /images/generate
//   RefX   → POST /images/refx     (1 reference + prompt, or a utility: bg/upscale/restore/colorize)
//   PixSync→ POST /images/pixsync  (up to 4 refs + optional scene + style)
// Prefills the Text mode from a Drift remix (recreate_payload).

import { useEffect, useState } from "react";
import { images, drift, models as modelsApi } from "../api";
import { uploadToTempUrl } from "../lib/upload";

const QUALITIES = ["standard", "hd", "4k"];
const ASPECTS = ["1:1", "9:16", "16:9", "4:5", "3:2"];
const REFX_MODES = [
  { id: "vibe_match", label: "Vibe match" },
  { id: "remove_background", label: "Remove BG" },
  { id: "upscale", label: "Upscale" },
  { id: "face_restore", label: "Face restore" },
  { id: "colorize", label: "Colorize" },
];

// A small uploader that returns a temp public URL.
function Uploader({ label, url, onUrl, onClear }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      onUrl(await uploadToTempUrl(file));
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="uploader">
      {url ? (
        <div className="uploader__thumb">
          <img src={url} alt={label} />
          <button onClick={onClear}>✕</button>
        </div>
      ) : (
        <label className="uploader__drop">
          {busy ? "Uploading…" : `+ ${label}`}
          <input type="file" accept="image/*" hidden onChange={pick} />
        </label>
      )}
    </div>
  );
}

export default function CreateImage({ user, onCreditsChange, remix, onNeedCredits }) {
  // Remix source: read from the post's top-level fields first, then its
  // recreate_payload (mirrors the mobile app's `item.X || recreate_payload.X`).
  const post = remix || {};
  const seed = post.recreate_payload || {};
  const genType = post.generation_type || seed.generation_type || "text_to_image";
  const initialMode = genType === "refx" ? "refx" : genType === "pixsync" ? "pixsync" : "text";

  const [mode, setMode] = useState(initialMode);

  const [prompt, setPrompt] = useState(seed.prompt || post.prompt || "");
  const [quality, setQuality] = useState(seed.quality || post.quality || "standard");
  const [aspect, setAspect] = useState(seed.aspect_ratio || post.aspect_ratio || "1:1");
  const [variations, setVariations] = useState(seed.variations || 1);
  const [listingId, setListingId] = useState(seed.listing_id || post.listing_id || "");
  const [modelList, setModelList] = useState([]);

  // RefX
  const [refUrl, setRefUrl] = useState(post.reference_image_url || seed.reference_image_url || "");
  const [refxMode, setRefxMode] = useState(seed.refx_mode || "vibe_match");
  const [strength, setStrength] = useState(seed.strength ?? 0.25);
  // PixSync
  const [refUrls, setRefUrls] = useState(post.reference_urls || seed.reference_urls || []);
  const [sceneUrl, setSceneUrl] = useState(post.scene_url || seed.scene_url || "");
  const [styleUrl, setStyleUrl] = useState(post.style_url || seed.style_url || "");
  const [adMode, setAdMode] = useState(!!seed.ad_mode);
  const [adType, setAdType] = useState(seed.ad_type || "");
  const [brandName, setBrandName] = useState(seed.brand_name || "");
  const [tagline, setTagline] = useState(seed.tagline || "");

  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [err, setErr] = useState("");
  const [lowCredits, setLowCredits] = useState(false);

  useEffect(() => {
    modelsApi.image().then((d) => setModelList(d.models || [])).catch(() => {});
  }, []);

  const handleErr = (e) => {
    if (e.detail?.error === "INSUFFICIENT_CREDITS") {
      setErr(`Not enough credits — you need ${e.detail.credits_required}, have ${e.detail.credits_available}.`);
      setLowCredits(true);
    } else setErr(e.message);
  };

  const run = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    setLowCredits(false);
    setResults([]);
    try {
      let res;
      if (mode === "text") {
        if (!prompt.trim()) throw new Error("Enter a prompt.");
        res = await images.generate({
          prompt, quality, aspect_ratio: aspect, variations: Number(variations),
          listing_id: listingId || undefined, source_drift_id: remix?._id, is_recreation: !!remix,
        });
      } else if (mode === "refx") {
        if (!refUrl) throw new Error("Upload a reference image.");
        res = await images.refx({
          image_url: refUrl, prompt, refx_mode: refxMode, strength: Number(strength),
          aspect_ratio: aspect, quality, variations: Number(variations),
          listing_id: listingId || undefined, source_drift_id: remix?._id, is_recreation: !!remix,
        });
      } else {
        if (refUrls.length === 0) throw new Error("Add at least one reference image.");
        res = await images.pixsync({
          reference_urls: refUrls, scene_url: sceneUrl || null, style_url: styleUrl || null,
          prompt, aspect_ratio: aspect, quality, variations: Number(variations),
          ad_mode: adMode, ad_type: adType || "", brand_name: brandName || "", tagline: tagline || "",
          listing_id: listingId || undefined, source_drift_id: remix?._id, is_recreation: !!remix,
        });
      }
      setResults(res.images || []);
      if (res.credits_remaining != null) onCreditsChange?.(res.credits_remaining);
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const postToDrift = async (img) => {
    try {
      await drift.share({ drop_id: img.image_id, type: "image" });
      alert("Shared to Drift!");
    } catch (e) {
      alert(e.message);
    }
  };

  const addRef = (u) => setRefUrls((xs) => (xs.length < 4 ? [...xs, u] : xs));
  const needsPrompt = mode === "text" || (mode === "refx" && refxMode === "vibe_match") || mode === "pixsync";

  return (
    <div className="page">
      <div className="page__head">
        <h1>Create image</h1>
        <p className="page__sub">{remix ? "Remixing a Drift post." : "Text to image, or edit from your own references."}</p>
      </div>

      <div className="tabs">
        <button className={`tab ${mode === "text" ? "tab--on" : ""}`} onClick={() => setMode("text")}>Text</button>
        <button className={`tab ${mode === "refx" ? "tab--on" : ""}`} onClick={() => setMode("refx")}>Reference</button>
        <button className={`tab ${mode === "pixsync" ? "tab--on" : ""}`} onClick={() => setMode("pixsync")}>Compose</button>
      </div>

      <div className="studio">
        <div className="studio__form">
          {mode === "refx" && (
            <>
              <div className="field"><span>Reference image</span><Uploader label="reference" url={refUrl} onUrl={setRefUrl} onClear={() => setRefUrl("")} /></div>
              <div className="field">
                <span>Tool</span>
                <div className="seg seg--wrap">
                  {REFX_MODES.map((m) => <button key={m.id} className={refxMode === m.id ? "seg__on" : ""} onClick={() => setRefxMode(m.id)}>{m.label}</button>)}
                </div>
              </div>
              {refxMode === "vibe_match" && (
                <label className="field">
                  <span>Change strength · {Number(strength).toFixed(2)}</span>
                  <input type="range" min="0" max="1" step="0.05" value={strength} onChange={(e) => setStrength(e.target.value)} />
                </label>
              )}
            </>
          )}

          {mode === "pixsync" && (
            <>
              <div className="field">
                <span>References (up to 4)</span>
                <div className="uploader-row">
                  {refUrls.map((u, i) => (
                    <div key={i} className="uploader__thumb"><img src={u} alt="" /><button onClick={() => setRefUrls((xs) => xs.filter((_, j) => j !== i))}>✕</button></div>
                  ))}
                  {refUrls.length < 4 && <Uploader label="ref" url="" onUrl={addRef} onClear={() => {}} />}
                </div>
              </div>
              <div className="field"><span>Scene (optional)</span><Uploader label="scene" url={sceneUrl} onUrl={setSceneUrl} onClear={() => setSceneUrl("")} /></div>
              <div className="field"><span>Style (optional)</span><Uploader label="style" url={styleUrl} onUrl={setStyleUrl} onClear={() => setStyleUrl("")} /></div>
              <button className={`toggle ${adMode ? "toggle--on" : ""}`} onClick={() => setAdMode((v) => !v)}>
                <span>🏷️ Ad mode (product shot)</span>
                <span className="toggle__knob" />
              </button>
              {adMode && (
                <>
                  <label className="field"><span>Ad type</span><input type="text" value={adType} onChange={(e) => setAdType(e.target.value)} placeholder="e.g. poster, banner, lifestyle" /></label>
                  <label className="field"><span>Brand name</span><input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} /></label>
                  <label className="field"><span>Tagline</span><input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} /></label>
                </>
              )}
            </>
          )}

          {needsPrompt && (
            <label className="field">
              <span>Prompt</span>
              <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe what you want…" />
            </label>
          )}

          {mode === "text" && modelList.length > 0 && (
            <div className="field">
              <span>Model</span>
              <select className="sort" style={{ width: "100%" }} value={listingId} onChange={(e) => setListingId(e.target.value)}>
                <option value="">Auto (best for quality)</option>
                {modelList.map((m) => <option key={m.listing_id} value={m.listing_id}>{m.display_name}</option>)}
              </select>
            </div>
          )}

          <div className="field">
            <span>Quality</span>
            <div className="seg">{QUALITIES.map((q) => <button key={q} className={quality === q ? "seg__on" : ""} onClick={() => setQuality(q)}>{q.toUpperCase()}</button>)}</div>
          </div>
          <div className="field">
            <span>Aspect ratio</span>
            <div className="seg seg--wrap">{ASPECTS.map((a) => <button key={a} className={aspect === a ? "seg__on" : ""} onClick={() => setAspect(a)}>{a}</button>)}</div>
          </div>
          <label className="field">
            <span>Variations · {variations}</span>
            <input type="range" min="1" max="6" step="1" value={variations} onChange={(e) => setVariations(e.target.value)} />
          </label>

          {err && <div className="form-error">{err}</div>}
          {lowCredits && <button className="btn btn--primary btn--block" onClick={onNeedCredits}>💎 Buy credits</button>}

          <button className="btn btn--primary btn--block" onClick={run} disabled={busy}>
            {busy ? "Generating…" : "✨ Generate"}
          </button>
        </div>

        <div className="studio__out">
          <span className="studio__outlabel">Results</span>
          <div className="results" style={{ "--ar": aspect.replace(":", "/") }}>
            {busy && results.length === 0
              ? Array.from({ length: Number(variations) }).map((_, i) => <div key={i} className="results__cell results__cell--load" />)
              : results.map((img, i) => (
                  <div key={i} className="results__cell">
                    <img src={img.image_url} alt={`result ${i + 1}`} />
                    <button className="results__share" onClick={() => postToDrift(img)}>Post to Drift</button>
                  </div>
                ))}
            {!busy && results.length === 0 && <div className="results__empty">Your images will appear here.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
